import React, { useState, useEffect, useMemo } from "react";
import {
  Textarea,
  Box,
  Heading,
  Flex,
  Button,
  useToast,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Spinner,
  Center,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import Select from "react-select";
import { FormHeader } from "../../../ui/FormHeader";
import { useDispatch, useSelector } from "react-redux";
import {
  addInventory,
  updateInventory,
  getInventoryDetailById,
} from "../../../redux/api/admin/InventoryApi";
import {
  addinventoryAction,
  updateinventoryAction,
} from "../../../redux/reduxSlice/admin/ProductSlice";
import { getCategoryList } from "../../../redux/reduxApi/admin/CategoryReduxApi";
import { getShadeNumberList } from "../../../redux/reduxApi/admin/ShadeReduxApi";
import { getEmployeesList } from "../../../redux/reduxApi/admin/EmployeesReduxApi";
import {
  itemUnitOptions,
} from "../utils/SearchPickList";
import { useLocation, useNavigate } from "react-router-dom";
import useSubCategorySearch from "../../../hook/useSubCategorySearch";
import { AsyncPaginate } from "react-select-async-paginate";
import { getLoggedInUserId } from "../../../../utils/authUtils";
import { getUserDetails } from "../../../../utils/IndexDB/PermissionStore";
import { formatDateTime } from "../../../../utils/commonFunctions";

function AddProductForm(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { category, status } = useSelector((state) => state.category);
  const loggedInUserId = getLoggedInUserId();
  const [userDetails, setUserDetails] = useState(null);
  const [createdByOptions, setCreatedByOptions] = useState([]);

  const [formData, setFormData] = useState({
    // Basic Information
    itemName: "",
    unit: "sqft",
    categoryName: null,
    subCategory: null,

    // Simple sellingPrice/Price field
    sellingPrice: "",
    createdBy: loggedInUserId ? String(loggedInUserId) : null,
    modifiedBy: null,
    createdByDetails: null,
    modifiedByDetails: null,
    CREATEDTIME: "",
    MODIFIEDTIME: "",

    // Description
    description: "",
  });

  const { loadSubCategories } = useSubCategorySearch({
    parentId: formData.categoryName?.value,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { closeDrawer } = props;
  const toast = useToast();
  const [currentPage] = useState(1);

  const isUpdateMode = location.state?.isUpdateMode;
  const productId = location.state?.id;

  useEffect(() => {
    const fetchUserDetails = async () => {
      const user = await getUserDetails();
      setUserDetails(user);
    };

    fetchUserDetails();
  }, []);

  useEffect(() => {
    dispatch(getEmployeesList({ page: 1, size: 30, limit: 30 }))
      .then((response) => {
        const employees = response.payload?.data?.employees || [];
        const options = employees.map((emp) => ({
          label: `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.email || "Unknown",
          value: emp.ROWID,
        }));
        setCreatedByOptions(options);
      })
      .catch((error) => {
        console.error("Error fetching employees for createdBy:", error);
      });
  }, [dispatch]);

  const loggedInUserOption = useMemo(() => {
    if (!loggedInUserId) return null;

    return {
      label: userDetails?.employeeName || "Current User",
      value: loggedInUserId,
    };
  }, [loggedInUserId, userDetails]);

  const selectedCreatedByOption = useMemo(() => {
    const targetCreatedBy = formData.createdBy;
    if (!targetCreatedBy) return null;

    const option = createdByOptions.find(
      (opt) => String(opt.value) === String(targetCreatedBy)
    );
    if (option) return option;

    if (formData.createdByDetails) {
      const details = formData.createdByDetails;
      const fullName = `${details.firstName || ""} ${details.lastName || ""}`.trim();
      return {
        label: fullName || details.employeeName || details.email || "Unknown",
        value: details.ROWID || targetCreatedBy,
      };
    }

    if (loggedInUserOption && String(loggedInUserOption.value) === String(targetCreatedBy)) {
      return loggedInUserOption;
    }

    if (typeof formData.createdBy === "string" && !/^\d+$/.test(formData.createdBy)) {
      return { label: formData.createdBy, value: formData.createdBy };
    }

    return null;
  }, [createdByOptions, formData.createdBy, formData.createdByDetails, loggedInUserOption]);

  const selectedModifiedByOption = useMemo(() => {
    const targetModifiedBy = formData.modifiedBy;

    if (targetModifiedBy) {
      const option = createdByOptions.find(
        (opt) => String(opt.value) === String(targetModifiedBy)
      );
      if (option) return option;

      if (loggedInUserOption && String(loggedInUserOption.value) === String(targetModifiedBy)) {
        return loggedInUserOption;
      }

      if (formData.modifiedByDetails) {
        const details = formData.modifiedByDetails;
        const fullName = `${details.firstName || ""} ${details.lastName || ""}`.trim();
        return {
          label: fullName || details.employeeName || details.email || "Unknown",
          value: details.ROWID || targetModifiedBy,
        };
      }

      if (typeof formData.modifiedBy === "string" && !/^\d+$/.test(formData.modifiedBy)) {
        return { label: formData.modifiedBy, value: formData.modifiedBy };
      }
    }

    if (isUpdateMode && loggedInUserOption) {
      return loggedInUserOption;
    }

    return null;
  }, [createdByOptions, formData.modifiedBy, formData.modifiedByDetails, isUpdateMode, loggedInUserOption]);

  useEffect(() => {
    // Simulate initial page load
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const categoryOptions = useMemo(() => {
    if (!category || category.length === 0) return [];

    // Filter items where parentCategoryId is null (these are parent categories)
    const parentCategories = category
      .filter(item => !item.parentCategoryId)
      .map(item => ({
        value: item.ROWID,
        label: item.name,
        rowId: item.ROWID
      }));

    return parentCategories;
  }, [category]);

  // const subCategoryOptions = useMemo(() => {
  //   if (!category || !formData.categoryName) return [];

  //   // Filter subcategories where parentCategoryId matches the selected category's ROWID
  //   const subCategories = category
  //     .filter(item => item.parentCategoryId === formData.categoryName)
  //     .map(item => ({
  //       value: item.ROWID,
  //       label: item.name,
  //       categoryId: item.ROWID
  //     }));

  //   return subCategories;
  // }, [category, formData.categoryName]);

  // Fetch categories on component mount
  useEffect(() => {
    dispatch(getCategoryList({ page: currentPage, size: 30, limit: 30 }));
  }, [dispatch, currentPage]);
  useEffect(() => {
    dispatch(getShadeNumberList({ page: currentPage, size: 30, limit: 30 }));
  }, [dispatch, currentPage]);

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.itemName?.trim()) {
      newErrors.itemName = "Item Name is required";
    }

    // Category validation
    if (!formData.categoryName) {
      newErrors.categoryName = "Category is required";
    }

    // Subcategory validation
    if (formData.categoryName && !formData.subCategory) {
      newErrors.subCategory = "Sub Category is required";
    }

    // sellingPrice validation - optional, but must be number if provided
    if (formData.sellingPrice && isNaN(parseFloat(formData.sellingPrice))) {
      newErrors.sellingPrice = "sellingPrice must be a number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getEmployeeIdValue = (value) => {
    if (!value) return null;
    if (typeof value === "object") {
      return value.ROWID || value.id || null;
    }
    return value;
  };

  useEffect(() => {
    if (isUpdateMode && productId) {
      setIsLoading(true);
      getInventoryDetailById(productId)
        .then((response) => {
          if (response.data.success) {
            const inventoryData = response.data.inventory[0];
            console.log("inventoryData@@##", inventoryData);

            const createdByValue =
              getEmployeeIdValue(inventoryData.createdUser) ||
              getEmployeeIdValue(inventoryData.createdBy) ||
              getEmployeeIdValue(inventoryData.createdByUser) ||
              loggedInUserId ||
              null;
            const modifiedByValue =
              getEmployeeIdValue(inventoryData.modifiedUser) ||
              getEmployeeIdValue(inventoryData.modifiedBy) ||
              getEmployeeIdValue(inventoryData.modifiedByUser) ||
              null;

            setFormData({
              itemName: inventoryData.itemName || "",
              unit: inventoryData.unit || "",
              categoryName: category.find(
                (cat) => cat.ROWID === inventoryData.categoryId
              )
                ? {
                    value: inventoryData.categoryId,
                    label: category.find(
                      (cat) => cat.ROWID === inventoryData.categoryId
                    )?.name,
                  }
                : null,

              subCategory: inventoryData.subCategoryId
                ? {
                    value: inventoryData.subCategoryId,
                    label: category.find(
                      (cat) => cat.ROWID === inventoryData.subCategoryId
                    )?.name,
                  }
                : null,
              sellingPrice: inventoryData.sellingPrice || inventoryData.sellingPrice || "",
              description: inventoryData.description || inventoryData.sellingDescription || "",
              createdBy: createdByValue ? String(createdByValue) : null,
              createdByDetails:
                inventoryData.createdUser ||
                (typeof inventoryData.createdBy === "object" ? inventoryData.createdBy : null) ||
                inventoryData.createdByUser ||
                null,
              modifiedBy: modifiedByValue ? String(modifiedByValue) : null,
              modifiedByDetails:
                inventoryData.modifiedUser ||
                (typeof inventoryData.modifiedBy === "object" ? inventoryData.modifiedBy : null) ||
                inventoryData.modifiedByUser ||
                null,
              CREATEDTIME: inventoryData.CREATEDTIME || "",
              MODIFIEDTIME: inventoryData.MODIFIEDTIME || "",
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching inventory details:", error);
          toast({
            title: "Error fetching inventory details",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isUpdateMode, productId, toast, category, loggedInUserId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    const newValue = selectedOption ? selectedOption.value : "";

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
      // Clear subCategory when category changes
      ...(name === "categoryName" && { subCategory: null })
    }));

    // Clear error when user selects
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Validate form before submission
    if (!validateForm()) {
      toast({
        title: "Please fill all required fields in the form",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      const excludedKeys = [
        "createdByDetails",
        "modifiedByDetails",
        "CREATEDTIME",
        "MODIFIEDTIME",
        "createdBy",
        "modifiedBy",
      ];

      // Append all text fields
      Object.keys(formData).forEach((key) => {
        if (excludedKeys.includes(key)) return;

        let value = formData[key];

        if (typeof value === "object" && value !== null) {
          value = value.value;
        }

        if (value !== "" && value !== null && value !== undefined && String(value).toLowerCase() !== "null" && String(value).toLowerCase() !== "undefined") {
          formDataToSend.append(key, value);
        }
      });

      if (!isUpdateMode) {
        const finalCreatedBy = formData.createdBy || loggedInUserId;
        if (finalCreatedBy && /^\d+$/.test(String(finalCreatedBy))) {
          formDataToSend.append("createdBy", String(finalCreatedBy));
        }
      }

      if (isUpdateMode && loggedInUserId && String(loggedInUserId).toLowerCase() !== "null" && String(loggedInUserId).toLowerCase() !== "undefined" && String(loggedInUserId) !== "") {
        formDataToSend.append("modifiedBy", String(loggedInUserId));
      }

      // Append ROWID for update
      if (isUpdateMode) {
        formDataToSend.append("ROWID", productId);
      }

      let response;

      if (isUpdateMode) {
        // Update existing inventory
        response = await updateInventory(productId, formDataToSend);
        dispatch(updateinventoryAction(response?.data?.inventory));
        toast({
          title: "Item updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new inventory
        response = await addInventory(formDataToSend);
        dispatch(addinventoryAction(response?.data?.inventory));
        toast({
          title: "Item created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      navigate(-1);
    } catch (error) {
      console.error("Error saving item:", error);
      toast({
        title: error.response?.data?.message || "Error saving item",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOnClose = () => {
    closeDrawer();
  };

  return (
    <>
      <Box className="h-[93vh] flex flex-col mb-32 overflow-y-auto">
        {/* Header */}
        <Box className="sticky top-0 z-10 bg-white">
          <FormHeader
            heading={"Mechanism"}
            onSubmit={handleSubmit}
            isUpdateMode={isUpdateMode}
            isSubmitting={isSubmitting}
          />
        </Box>

        {isLoading ? (
          <Center flex="1" flexDirection="column" gap={4}>
            <div className="spinner-overlay">
              <Spinner size="xl" color="#011638" />
            </div>
          </Center>
        ) : (
          <>
            {/* Form Content */}
            <Box className="flex-1 px-4 py-2">
              <div className="row">
                <div className="col-md-12">
                  {/* Item Information Section */}
                  <div className="card mb-1">
                    <div className="card-body">
                      <Heading as="h3" size="md" mb={4}>
                        Mechanism Information
                      </Heading>

                      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        {/* Row 1: Mechanism Name - Full width */}
                        <GridItem colSpan={1}>
                          <FormControl isInvalid={!!errors.itemName}>
                            <FormLabel htmlFor="itemName">
                              Mechanism Name <span style={{ color: "red" }}>*</span>
                            </FormLabel>
                            <Input
                              type="text"
                              className="form-control"
                              name="itemName"
                              placeholder="Enter Mechanism Name"
                              value={formData.itemName || ""}
                              onChange={handleChange}
                            />
                            <FormErrorMessage>{errors.itemName}</FormErrorMessage>
                          </FormControl>
                        </GridItem>

                        {/* Row 2: Category Name and Sub Category */}
                        <GridItem>
                          <FormControl isInvalid={!!errors.categoryName}>
                            <FormLabel htmlFor="categoryName">
                              Category Name <span style={{ color: "red" }}>*</span>
                            </FormLabel>
                            <Select
                              options={categoryOptions}
                              placeholder="Select Category"
                              value={formData.categoryName || null}
                              onChange={(selected) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  categoryName: selected,
                                  subCategory: null,
                                }))
                              }
                              isClearable
                              isLoading={status === "loading"}
                              isDisabled={status === "loading"}
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  cursor: "pointer",
                                  borderColor: errors.categoryName ? '#E53E3E' : base.borderColor,
                                }),
                                option: (base) => ({
                                  ...base,
                                  cursor: "pointer",
                                }),
                              }}
                            />
                            <FormErrorMessage>{errors.categoryName}</FormErrorMessage>
                          </FormControl>
                        </GridItem>

                        <GridItem>
                          <FormControl isInvalid={!!errors.subCategory}>
                            <FormLabel htmlFor="subCategory">
                              Sub Category <span style={{ color: "red" }}>*</span>
                            </FormLabel>
                            <AsyncPaginate
                              key={`sub-${formData.categoryName?.value || "none"}`}
                              loadOptions={loadSubCategories}
                              additional={{ page: 1 }}
                              debounceTimeout={300}
                              isClearable
                              placeholder={
                                formData.categoryName
                                  ? "Search Sub Category"
                                  : "Select Category First"
                              }
                              value={formData.subCategory || null}
                              onChange={(selected) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  subCategory: selected || null,
                                }))
                              }
                              isDisabled={!formData.categoryName}
                            />
                            <FormErrorMessage>{errors.subCategory}</FormErrorMessage>
                          </FormControl>
                        </GridItem>

                        {/* Row 3: Unit and Selling Price */}
                        <GridItem>
                          <FormControl>
                            <FormLabel htmlFor="unit">Unit</FormLabel>
                            <Select
                              options={itemUnitOptions}
                              isClearable
                              placeholder="Select Unit"
                              value={itemUnitOptions.find(
                                (option) => option.value === formData.unit
                              )}
                              onChange={(selected) =>
                                handleSelectChange("unit", selected)
                              }
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  cursor: "pointer",
                                }),
                                option: (base) => ({
                                  ...base,
                                  cursor: "pointer",
                                }),
                              }}
                            />
                            <FormErrorMessage>{errors.unit}</FormErrorMessage>
                          </FormControl>
                        </GridItem>

                        <GridItem>
                          <FormControl isInvalid={!!errors.sellingPrice}>
                            <FormLabel htmlFor="sellingPrice">Selling Price (tax inclusive)</FormLabel>
                            <Input
                              type="number"
                              step="0.01"
                              className="form-control"
                              name="sellingPrice"
                              placeholder="Enter Selling Price"
                              value={formData.sellingPrice || ""}
                              onChange={handleChange}
                            />
                            <FormErrorMessage>{errors.sellingPrice}</FormErrorMessage>
                          </FormControl>
                        </GridItem>

                        <GridItem>
                          <FormControl>
                            <FormLabel htmlFor="createdBy">Created By</FormLabel>
                            <AsyncPaginate
                              value={selectedCreatedByOption}
                              loadOptions={async (search) => ({
                                options: createdByOptions.filter((option) =>
                                  option.label.toLowerCase().includes(search?.toLowerCase() || "")
                                ),
                              })}
                              onChange={(selected) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  createdBy: selected ? String(selected.value) : null,
                                }))
                              }
                              isDisabled={isUpdateMode}
                              placeholder="Created By"
                            />
                          </FormControl>
                        </GridItem>

                        <GridItem>
                          <FormControl>
                            <FormLabel htmlFor="modifiedBy">Modified By</FormLabel>
                            <AsyncPaginate
                              value={selectedModifiedByOption}
                              loadOptions={async (search) => ({
                                options: createdByOptions.filter((option) =>
                                  option.label.toLowerCase().includes(search?.toLowerCase() || "")
                                ),
                              })}
                              isDisabled={true}
                              placeholder={isUpdateMode ? "Modified By" : "-"}
                            />
                          </FormControl>
                        </GridItem>

                        <GridItem>
                          <FormControl>
                            <FormLabel htmlFor="CREATEDTIME">Created Time</FormLabel>
                            <Input
                              type="text"
                              className="form-control"
                              name="CREATEDTIME"
                              value={formatDateTime(formData.CREATEDTIME)}
                              isReadOnly
                              isDisabled
                            />
                          </FormControl>
                        </GridItem>

                        <GridItem>
                          <FormControl>
                            <FormLabel htmlFor="MODIFIEDTIME">Modified Time</FormLabel>
                            <Input
                              type="text"
                              className="form-control"
                              name="MODIFIEDTIME"
                              value={formatDateTime(formData.MODIFIEDTIME)}
                              isReadOnly
                              isDisabled
                            />
                          </FormControl>
                        </GridItem>

                        {/* Row 4: Description - Full width */}
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel htmlFor="description">Description</FormLabel>
                            <Textarea
                              rows={4}
                              className="form-control"
                              name="description"
                              placeholder="Enter Description"
                              value={formData.description || ""}
                              onChange={handleChange}
                            />
                          </FormControl>
                        </GridItem>
                      </Grid>
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          </>
        )}
      </Box>
    </>
  );
}

export default AddProductForm;